import { formatDate } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { Permissions } from '../../../../constants/permissions';
import { IControllerDetail } from '../../../../models/controller';
import { Auth } from '../../../../services/auth';
import { Functions } from '../../../../services/shared/functions';
import { IconsStyles } from '../../../../services/shared/icons-styles';

@Component({
  selector: 'app-controller-table',
  imports: [TableModule, PanelModule],
  templateUrl: './controller-table.html',
  styleUrl: './controller-table.css',
})
export class ControllerTable {
  controller = input.required<IControllerDetail>();
  isLoading = input.required<boolean>();
  readonly locale = inject(LOCALE_ID);
  readonly iconsStyles = inject(IconsStyles);
  readonly customFunctions = inject(Functions);
  readonly shouldBeCollapsed = this.customFunctions.shouldBeCollapsed();
  private authService = inject(Auth);

  tableData = signal<{ description: string; specification: string }[]>([]);

  constructor() {
    effect(() => {
      const ctrl = this.controller();
      if (!ctrl) {
        this.tableData.set([]);
        return;
      }

      const tempArr = [];
      tempArr.push(
        {
          description: 'System Status',
          specification: ctrl.deviceStatus,
        },
        { description: 'Name', specification: ctrl.name }
      );

      if (this.authService.hasPermission(Permissions.see_device_type)) {
        tempArr.push({
          description: 'Device Type',
          specification: ctrl.deviceType,
        });
      }

      tempArr.push(
        { description: 'Serial Number', specification: ctrl.serialNumber },
        { description: 'Installation', specification: ctrl.installationName }
      );

      if (this.authService.hasPermission(Permissions.see_main_fw_version)) {
        tempArr.push({
          description: 'Main FW Version',
          specification: this.customFunctions.getShortenedMainFW(
            ctrl.mainFWVersion
          ),
        });
      }

      if (this.authService.hasPermission(Permissions.see_io_fw_version)) {
        tempArr.push({
          description: 'IO FW Version',
          specification: ctrl.ioFWVersion,
        });
      }

      if (
        this.authService.hasPermission(Permissions.see_device_client_version)
      ) {
        tempArr.push({
          description: 'Device Client Version',
          specification: ctrl.deviceClientVersion,
        });
      }

      tempArr.push({
        description: 'Last Update',
        specification: formatDate(
          ctrl.lastUpdate,
          'yyyy-MM-dd HH:mm:ss',
          this.locale
        ),
      });

      if (this.authService.hasPermission(Permissions.see_board_voltage)) {
        tempArr.push({
          description: 'Board Voltage',
          specification: ctrl.boardVoltage ? '1' : '0',
        });
      }

      if (this.authService.hasPermission(Permissions.see_board_temperature)) {
        tempArr.push({
          description: 'Board Temperature (℃)',
          specification: ctrl.boardTemp.toString(),
        });
      }

      if (this.authService.hasPermission(Permissions.see_io_com_error)) {
        tempArr.push({
          description: 'IO Com Error',
          specification: ctrl.ioComError ? '1' : '0',
        });
      }

      tempArr.push(
        {
          description: 'Grid import limit',
          specification: ctrl.gridImportLimit.toString(),
        },
        {
          description: 'Grid export limit',
          specification: ctrl.gridExportLimit.toString(),
        }
      );

      if (this.authService.hasPermission(Permissions.see_activation_info)) {
        tempArr.push({
          description: 'Activated on',
          specification: ctrl.activatedOn
            ? formatDate(ctrl.activatedOn, 'yyyy-MM-dd HH:mm:ss', this.locale)
            : 'Unknown',
        });
      }

      this.tableData.set(tempArr);
    });
  }
}
